import type { TFunction } from 'i18next';
import type { DeskSource, SectorDesk, ServiceDesk } from 'api/sectorAlignmentApi';

type UserId = string | number | null | undefined;

const storageKey = (userId: string | number) => `sector-alignment:desks:${userId}`;

// R8: blocked or full storage reads as nothing saved, and the selection is simply not remembered.
const safely = <T>(fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

export const sameSet = (a: SectorDesk[], b: SectorDesk[]): boolean => a.length === b.length && a.every((sd) => b.includes(sd));

export const sourceLabel = (t: TFunction, source: DeskSource | null): string =>
  source === 'globe-data' ? t('Globe Data') : source === 'jira-assets' ? t('Jira Assets') : t('Not applicable');

/** AC19: saved desks still applicable, deduped, in saved order; nothing left → every applicable desk, not « restored ». */
export const cleanSelection = (saved: SectorDesk[] | null | undefined, desks: ServiceDesk[]): { selected: SectorDesk[]; restored: boolean } => {
  const applicable = desks.filter((d) => d.applicable).map((d) => d.sd);
  const kept = Array.from(new Set((saved ?? []).filter((sd) => applicable.includes(sd))));
  return kept.length ? { selected: kept, restored: true } : { selected: applicable, restored: false };
};

export const readSelection = (userId: UserId): SectorDesk[] | null =>
  userId == null
    ? null
    : safely(() => {
        const parsed: unknown = JSON.parse(localStorage.getItem(storageKey(userId)) ?? 'null');
        return Array.isArray(parsed) ? parsed.filter((sd): sd is number => Number.isInteger(sd)) : null;
      }, null);

export const writeSelection = (userId: UserId, sds: SectorDesk[]): void => {
  if (userId != null) safely(() => localStorage.setItem(storageKey(userId), JSON.stringify(sds)), undefined);
};

/** « Réinitialiser »: forgets the saved selection so the next visit starts from the defaults. */
export const clearSelection = (userId: UserId): void => {
  if (userId != null) safely(() => localStorage.removeItem(storageKey(userId)), undefined);
};

/** « Relancer l'analyse » only when the current analysis covers exactly the selection. */
export const buttonLabelState = (selected: SectorDesk[], analysed: SectorDesk[]): 'analyse' | 'relaunch' =>
  selected.length > 0 && sameSet(selected, analysed) ? 'relaunch' : 'analyse';

/** AC20: the desk being realigned stays selected. */
export const withLocked = (selected: SectorDesk[], lockedSd: SectorDesk | null | undefined): SectorDesk[] =>
  lockedSd == null || selected.includes(lockedSd) ? selected : [...selected, lockedSd];
